import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, PlusCircle, Trash2 } from 'lucide-react';
import { apiKeysApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input, Label } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import type { Role } from '../lib/types';

const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', operator: 'Operator', viewer: 'Viewer' };

function CreateKeyForm({ onCreated }: { onCreated: (rawKey: string) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => apiKeysApi.create({ name, role }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      onCreated(result.rawKey);
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <Label htmlFor="key-name">Name</Label>
        <Input id="key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="CI pipeline" required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="key-role">Role for this key</Label>
        <Select value={role} onValueChange={(v) => setRole(v as Role)}>
          <SelectTrigger id="key-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(ROLE_LABEL) as Array<[Role, string]>).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={mutation.isPending || !name.trim()}>
        Create key
      </Button>
    </form>
  );
}

function RevealKey({ rawKey, onClose }: { rawKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">
        This is the only time you'll see this key in full. Copy it now and store it somewhere safe.
      </p>
      <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-surface-sunken px-3 py-2">
        <code className="truncate text-sm text-ink">{rawKey}</code>
        <Button variant="ghost" size="sm" onClick={copy}>
          <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <Button className="w-full" onClick={onClose}>
        Done
      </Button>
    </div>
  );
}

export function ApiKeys() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revealKey, setRevealKey] = useState<string | null>(null);
  const query = useQuery({ queryKey: ['api-keys'], queryFn: () => apiKeysApi.list() });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast({ title: 'Key revoked', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not revoke key', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const keys = query.data?.apiKeys ?? [];

  return (
    <div>
      <PageHeader
        title="API Keys"
        description="For service accounts and automation — each key acts with a fixed role."
        action={
          <Dialog
            open={createOpen || revealKey !== null}
            onOpenChange={(open) => {
              if (!open) {
                setCreateOpen(false);
                setRevealKey(null);
              } else {
                setCreateOpen(true);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <PlusCircle size={16} /> New API key
              </Button>
            </DialogTrigger>
            <DialogContent title={revealKey ? 'Key created' : 'New API key'} description={revealKey ? undefined : 'Choose a name and a role for this key.'}>
              {revealKey ? (
                <RevealKey
                  rawKey={revealKey}
                  onClose={() => {
                    setRevealKey(null);
                    setCreateOpen(false);
                  }}
                />
              ) : (
                <CreateKeyForm
                  onCreated={(rawKey) => {
                    setCreateOpen(false);
                    setRevealKey(rawKey);
                  }}
                />
              )}
            </DialogContent>
          </Dialog>
        }
      />

      {keys.length === 0 ? (
        <EmptyState icon={<KeyRound size={32} />} title="No API keys yet" description="Create one for CI/CD or another automated integration." />
      ) : (
        <div className="space-y-2">
          {keys.map((key) => (
            <Card key={key.id}>
              <CardContent className="flex items-center justify-between pt-6">
                <div>
                  <p className="font-medium text-ink">{key.name}</p>
                  <p className="text-xs text-ink-muted">
                    {key.keyPrefix}••••••••· created {new Date(key.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="brand">{ROLE_LABEL[key.role]}</Badge>
                  {key.revokedAt ? (
                    <Badge variant="danger">Revoked</Badge>
                  ) : (
                    <ConfirmDialog
                      trigger={
                        <Button variant="ghost" size="icon" aria-label="Revoke key">
                          <Trash2 size={16} className="text-danger" />
                        </Button>
                      }
                      title="Revoke this key?"
                      description={`"${key.name}" will stop working immediately. Any automation using it will fail until replaced with a new key.`}
                      confirmLabel="Revoke"
                      variant="danger"
                      onConfirm={() => revokeMutation.mutateAsync(key.id)}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
